import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  Shield, AlertTriangle, CheckCircle, Clock, TrendingUp,
  FileText, Building2, Calendar, AlertCircle, ChevronRight, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('corpcraft_token');
      const res = await fetch(`${API}/api/compliance/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCompliance(data);
      } else {
        setError('Failed to load compliance data');
      }
    } catch (err) {
      setError('Network error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getComplianceLevelConfig = (level) => {
    const configs = {
      excellent: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: 'Excellent' },
      good: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: TrendingUp, label: 'Good' },
      warning: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: AlertTriangle, label: 'Warning' },
      critical: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle, label: 'Critical' }
    };
    return configs[level] || configs.warning;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { color: 'text-red-400 bg-red-500/10', icon: AlertCircle },
      warning: { color: 'text-yellow-400 bg-yellow-500/10', icon: AlertTriangle },
      info: { color: 'text-blue-400 bg-blue-500/10', icon: Info }
    };
    return configs[priority] || configs.info;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !compliance) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <p className="text-red-400">{error || 'Failed to load compliance data'}</p>
        <Button onClick={loadComplianceData} className="mt-4">Retry</Button>
      </div>
    );
  }

  const levelConfig = getComplianceLevelConfig(compliance.compliance_level);
  const LevelIcon = levelConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
          <Shield className="w-8 h-8 text-blue-400" />
          Compliance Dashboard
        </h1>
        <p className="text-slate-400 mt-2">Real-time entity and filing compliance monitoring</p>
      </div>

      {/* Overall Score Card */}
      <div className={`border rounded-lg p-6 ${levelConfig.color}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <LevelIcon className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Overall Compliance: {levelConfig.label}</h2>
            </div>
            <p className="text-sm opacity-80">
              {compliance.total_entities} Entities • {compliance.total_ucc_filings} UCC Filings
            </p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold">{compliance.overall_score}%</div>
            <p className="text-sm opacity-80 mt-1">Compliance Score</p>
          </div>
        </div>
      </div>

      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-red-500/30 rounded-lg p-4 bg-red-500/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-red-400 font-medium">Critical Alerts</span>
            <AlertCircle className="w-5 h-5 text-red-400" />
          </div>
          <div className="text-3xl font-bold text-red-400">{compliance.alerts.critical}</div>
        </div>
        
        <div className="border border-yellow-500/30 rounded-lg p-4 bg-yellow-500/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-yellow-400 font-medium">Warnings</span>
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="text-3xl font-bold text-yellow-400">{compliance.alerts.warning}</div>
        </div>
        
        <div className="border border-blue-500/30 rounded-lg p-4 bg-blue-500/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-400 font-medium">Info Items</span>
            <Info className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-blue-400">{compliance.alerts.info}</div>
        </div>
      </div>

      {/* Compliance Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Entity Compliance */}
        <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-purple-400" />
            <h3 className="text-xl font-semibold">Entity Compliance</h3>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-400">Compliance Score</span>
                <span className="font-bold text-purple-400">{compliance.entity_compliance.score}%</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${compliance.entity_compliance.score}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1">Compliant</p>
                <p className="text-2xl font-bold text-green-400">{compliance.entity_compliance.compliant_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1">Issues</p>
                <p className="text-2xl font-bold text-red-400">{compliance.entity_compliance.non_compliant_count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* UCC Compliance */}
        <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-semibold">UCC Filing Compliance</h3>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-400">Compliance Score</span>
                <span className="font-bold text-blue-400">{compliance.ucc_compliance.score}%</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${compliance.ucc_compliance.score}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1">Active</p>
                <p className="text-xl font-bold text-green-400">{compliance.ucc_compliance.active_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1">Expiring</p>
                <p className="text-xl font-bold text-yellow-400">{compliance.ucc_compliance.expiring_soon_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1">Expired</p>
                <p className="text-xl font-bold text-red-400">{compliance.ucc_compliance.expired_count}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      {compliance.action_items.length > 0 && (
        <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Action Items ({compliance.action_items.length})
          </h3>
          <div className="space-y-3">
            {compliance.action_items.map((item, idx) => {
              const priorityConfig = getPriorityConfig(item.priority);
              const PriorityIcon = priorityConfig.icon;
              
              return (
                <div
                  key={idx}
                  className="border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded ${priorityConfig.color}`}>
                      <PriorityIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-semibold">{item.title}</h4>
                        {item.days_remaining !== undefined && (
                          <span className="text-sm text-slate-400">
                            {item.days_remaining} days remaining
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mb-2">{item.description}</p>
                      {item.entity_name && (
                        <p className="text-xs text-slate-500">Entity: {item.entity_name}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expiring Soon */}
      {compliance.expiring_soon.length > 0 && (
        <div className="border border-slate-800 rounded-lg p-6 bg-slate-900/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-400" />
            Expiring Soon ({compliance.expiring_soon.length})
          </h3>
          <div className="space-y-3">
            {compliance.expiring_soon.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 border border-slate-700 rounded-lg"
              >
                <div className="flex-1">
                  <p className="font-medium">{item.item_name}</p>
                  <p className="text-sm text-slate-400">{item.type.replace('_', ' ').toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${item.priority === 'critical' ? 'text-red-400' : 'text-yellow-400'}`}>
                    {item.days_remaining} days
                  </p>
                  <p className="text-xs text-slate-500">{item.action_required}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="text-center text-sm text-slate-500">
        Last updated: {new Date(compliance.last_updated).toLocaleString()}
      </div>
    </div>
  );
}
